import { z } from 'zod';
import { reaisParaCentavos } from './estoque';
import { parcelaPriceCentavos } from './juros';

// ─────────── Fase 4 · Financeiro da loja (docs/07) ───────────

const texto = (max: number) => z.string().trim().max(max);
const textoOpc = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const centavos = z.number().int().min(0).max(100_000_000_00);
const positivo = centavos.refine((v) => v > 0, 'Valor deve ser maior que zero');
const dia = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (AAAA-MM-DD)')
  .refine((v) => !Number.isNaN(new Date(`${v}T00:00:00Z`).getTime()), 'Data inválida');

export const TIPOS_LANCAMENTO = ['pagar', 'receber'] as const;
export type TipoLancamento = (typeof TIPOS_LANCAMENTO)[number];
export const STATUS_LANCAMENTO = ['aberto', 'pago', 'cancelado'] as const;
export type StatusLancamento = (typeof STATUS_LANCAMENTO)[number];

export const GRUPOS_DRE = [
  'venda_veiculo',
  'compra_veiculo',
  'custo_veiculo',
  'receita_financiamento',
  'receita_extra',
  'comissao_equipe',
  'despesa_fixa',
  'despesa_variavel',
  'imposto',
  'financeiro',
  'transferencia',
  'outros',
] as const;
export type GrupoDre = (typeof GRUPOS_DRE)[number];

/**
 * Grupos que só movimentam o caixa: o DRE usa a própria venda (receita e custo congelado)
 * e a tabela de comissões, para não contar duas vezes.
 */
export const GRUPOS_SO_CAIXA: readonly GrupoDre[] = [
  'venda_veiculo',
  'compra_veiculo',
  'custo_veiculo',
  'comissao_equipe',
  'transferencia',
];

export const NOME_GRUPO_DRE: Record<GrupoDre, string> = {
  venda_veiculo: 'Venda de carros',
  compra_veiculo: 'Compra de carros',
  custo_veiculo: 'Preparação de carros',
  receita_financiamento: 'Retorno de financiamento',
  receita_extra: 'Outras receitas',
  comissao_equipe: 'Comissões da equipe',
  despesa_fixa: 'Despesas fixas',
  despesa_variavel: 'Despesas variáveis',
  imposto: 'Impostos',
  financeiro: 'Despesas financeiras',
  transferencia: 'Transferência entre contas',
  outros: 'Outros',
};

/** Categorias criadas para toda loja no primeiro uso. As de `codigo` são usadas pelos lançamentos automáticos. */
export const CATEGORIAS_PADRAO: {
  codigo: string;
  nome: string;
  tipo: TipoLancamento;
  grupo: GrupoDre;
}[] = [
  { codigo: 'venda_veiculo', nome: 'Venda de carro', tipo: 'receber', grupo: 'venda_veiculo' },
  {
    codigo: 'financiado_banco',
    nome: 'Valor financiado (banco)',
    tipo: 'receber',
    grupo: 'venda_veiculo',
  },
  { codigo: 'sinal_reserva', nome: 'Sinal de reserva', tipo: 'receber', grupo: 'venda_veiculo' },
  {
    codigo: 'retorno_financiamento',
    nome: 'Retorno do banco',
    tipo: 'receber',
    grupo: 'receita_financiamento',
  },
  {
    codigo: 'comissao_protecao',
    nome: 'Comissão de garantia e seguro',
    tipo: 'receber',
    grupo: 'receita_extra',
  },
  // acessórios entram no DRE pela própria venda; o lançamento é só o recebimento
  { codigo: 'acessorios', nome: 'Acessórios vendidos', tipo: 'receber', grupo: 'venda_veiculo' },
  // cartão: o que a maquininha vai pagar (caixa) e a taxa (despesa financeira)
  {
    codigo: 'cartao_receber',
    nome: 'Cartão a receber (maquininha)',
    tipo: 'receber',
    grupo: 'venda_veiculo',
  },
  { codigo: 'taxa_cartao', nome: 'Taxa da maquininha', tipo: 'pagar', grupo: 'financeiro' },
  // boleto, cheque e crediário: parcelas do cliente; os juros do crediário entram no DRE à parte
  {
    codigo: 'parcela_cliente',
    nome: 'Parcelas do cliente (boleto, cheque, crediário)',
    tipo: 'receber',
    grupo: 'venda_veiculo',
  },
  { codigo: 'quebra_caixa', nome: 'Diferença de caixa', tipo: 'pagar', grupo: 'outros' },
  { codigo: 'sobra_caixa', nome: 'Sobra de caixa', tipo: 'receber', grupo: 'receita_extra' },
  { codigo: 'outras_receitas', nome: 'Outras receitas', tipo: 'receber', grupo: 'receita_extra' },
  { codigo: 'compra_veiculo', nome: 'Compra de carro', tipo: 'pagar', grupo: 'compra_veiculo' },
  {
    codigo: 'custo_veiculo',
    nome: 'Preparação do carro',
    tipo: 'pagar',
    grupo: 'custo_veiculo',
  },
  {
    codigo: 'comissao_equipe',
    nome: 'Comissão da equipe',
    tipo: 'pagar',
    grupo: 'comissao_equipe',
  },
  { codigo: 'aluguel', nome: 'Aluguel', tipo: 'pagar', grupo: 'despesa_fixa' },
  { codigo: 'salarios', nome: 'Salários e encargos', tipo: 'pagar', grupo: 'despesa_fixa' },
  { codigo: 'energia', nome: 'Energia, água e internet', tipo: 'pagar', grupo: 'despesa_fixa' },
  { codigo: 'sistemas', nome: 'Sistemas e assinaturas', tipo: 'pagar', grupo: 'despesa_fixa' },
  { codigo: 'marketing', nome: 'Marketing e anúncios', tipo: 'pagar', grupo: 'despesa_variavel' },
  { codigo: 'eventos', nome: 'Eventos da loja', tipo: 'pagar', grupo: 'despesa_variavel' },
  {
    codigo: 'pos_venda',
    nome: 'Garantia e pós-venda de carro',
    tipo: 'pagar',
    grupo: 'despesa_variavel',
  },
  { codigo: 'impostos', nome: 'Impostos', tipo: 'pagar', grupo: 'imposto' },
  // multa paga ao órgão; o repasse ao responsável (comprador, test drive) volta como receita
  { codigo: 'multas', nome: 'Multas de trânsito', tipo: 'pagar', grupo: 'despesa_variavel' },
  { codigo: 'repasse_multa', nome: 'Repasse de multa', tipo: 'receber', grupo: 'receita_extra' },
  { codigo: 'tarifas', nome: 'Tarifas e juros', tipo: 'pagar', grupo: 'financeiro' },
  { codigo: 'outras_despesas', nome: 'Outras despesas', tipo: 'pagar', grupo: 'outros' },
  {
    codigo: 'transferencia_saida',
    nome: 'Transferência enviada',
    tipo: 'pagar',
    grupo: 'transferencia',
  },
  {
    codigo: 'transferencia_entrada',
    nome: 'Transferência recebida',
    tipo: 'receber',
    grupo: 'transferencia',
  },
];

// ─────────── Schemas ───────────

export const contaFinanceiraSchema = z.object({
  filialId: z.string().uuid(),
  nome: texto(60).min(2),
  tipo: z.enum(['caixa', 'banco']),
  banco: textoOpc(60),
  saldoInicialCentavos: z.number().int().min(-100_000_000_00).max(100_000_000_00).default(0),
  saldoInicialEm: dia,
  padrao: z.boolean().default(false),
});

export const categoriaFinanceiraSchema = z.object({
  nome: texto(60).min(2),
  tipo: z.enum(TIPOS_LANCAMENTO),
  grupo: z.enum(GRUPOS_DRE).refine((g) => !GRUPOS_SO_CAIXA.includes(g), {
    message: 'Este grupo é usado só pelos lançamentos automáticos',
  }),
});

/** Rateio entre filiais: percentuais em centésimos de ponto (10000 = 100%). */
export const rateioSchema = z
  .array(z.object({ filialId: z.string().uuid(), percentual: z.number().int().min(1).max(10_000) }))
  .min(2)
  .max(50)
  .refine((r) => r.reduce((s, x) => s + x.percentual, 0) === 10_000, {
    message: 'O rateio precisa somar 100%',
  })
  .refine((r) => new Set(r.map((x) => x.filialId)).size === r.length, {
    message: 'Filial repetida no rateio',
  });
export type Rateio = z.infer<typeof rateioSchema>;

export const lancamentoCriarSchema = z
  .object({
    tipo: z.enum(TIPOS_LANCAMENTO),
    categoriaId: z.string().uuid(),
    descricao: texto(160).min(2),
    valorCentavos: positivo,
    vencimento: dia,
    competencia: dia.optional(),
    filialId: z.string().uuid().optional(),
    /** despesa do grupo dividida entre filiais */
    rateio: rateioSchema.optional(),
    parcelas: z.number().int().min(1).max(60).default(1),
    contraparte: textoOpc(120),
    veiculoId: z.string().uuid().optional(),
    clienteId: z.string().uuid().optional(),
    observacoes: textoOpc(500),
    /** já pago/recebido (lança e baixa de uma vez) */
    pago: z.object({ contaId: z.string().uuid(), pagoEm: dia, forma: textoOpc(30) }).optional(),
  })
  .refine((d) => d.filialId || d.rateio, { message: 'Informe a filial ou o rateio' });
export type LancamentoCriar = z.infer<typeof lancamentoCriarSchema>;

export const lancamentoAtualizarSchema = z.object({
  descricao: texto(160).min(2).optional(),
  valorCentavos: positivo.optional(),
  vencimento: dia.optional(),
  competencia: dia.optional(),
  categoriaId: z.string().uuid().optional(),
  contraparte: textoOpc(120),
  observacoes: textoOpc(500),
});

export const baixaSchema = z.object({
  contaId: z.string().uuid(),
  pagoEm: dia,
  /** diferente do valor = juros/desconto; padrão: o valor do lançamento */
  valorPagoCentavos: positivo.optional(),
  forma: textoOpc(30),
});

/** filialId null = despesa do grupo, rateada pelos percentuais ou em partes iguais entre as filiais. */
export const despesaRecorrenteSchema = z.object({
  categoriaId: z.string().uuid(),
  descricao: texto(120).min(2),
  valorCentavos: positivo,
  diaVencimento: z.number().int().min(1).max(28),
  filialId: z.string().uuid().nullable(),
  rateio: rateioSchema.nullable().optional(),
  inicio: z.string().regex(/^\d{4}-\d{2}$/, 'Mês inválido (AAAA-MM)'),
});

export const transferenciaContasSchema = z
  .object({
    deContaId: z.string().uuid(),
    paraContaId: z.string().uuid(),
    valorCentavos: positivo,
    data: dia,
    descricao: textoOpc(120),
  })
  .refine((d) => d.deContaId !== d.paraContaId, { message: 'Escolha contas diferentes' });

export const conciliarSchema = z.object({
  linhaId: z.string().uuid(),
  lancamentoId: z.string().uuid().nullable(),
  /** sem lançamento: cria um a partir da linha nesta categoria */
  categoriaId: z.string().uuid().optional(),
  ignorar: z.boolean().optional(),
});

// ─────────── Cálculos (puros, testados) ───────────

/** Divide um valor em N parcelas inteiras; a diferença de centavos vai na primeira. */
export function dividirEmParcelas(valor: number, n: number): number[] {
  const base = Math.floor(valor / n);
  const resto = valor - base * n;
  return Array.from({ length: n }, (_, i) => base + (i === 0 ? resto : 0));
}

/** Rateia um valor pelos percentuais (centésimos); o arredondamento fica com a maior fatia. */
export function ratear(
  valor: number,
  rateio: Rateio,
): { filialId: string; valorCentavos: number }[] {
  const partes = rateio.map((r) => ({
    filialId: r.filialId,
    valorCentavos: Math.floor((valor * r.percentual) / 10_000),
  }));
  const diferenca = valor - partes.reduce((s, p) => s + p.valorCentavos, 0);
  const maior = rateio.reduce((m, r, i) => (r.percentual > rateio[m]!.percentual ? i : m), 0);
  partes[maior]!.valorCentavos += diferenca;
  return partes.filter((p) => p.valorCentavos > 0);
}

/** Rateio em partes iguais (para despesa do grupo sem percentuais). */
export function rateioIgual(filialIds: string[]): Rateio {
  const n = filialIds.length;
  const base = Math.floor(10_000 / n);
  return filialIds.map((filialId, i) => ({
    filialId,
    percentual: base + (i === 0 ? 10_000 - base * n : 0),
  }));
}

/** Soma N meses a uma data AAAA-MM-DD mantendo o dia (limitado ao fim do mês). */
export function somarMeses(iso: string, meses: number): string {
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number];
  const alvo = new Date(Date.UTC(a, m - 1 + meses, 1));
  const ultimo = new Date(Date.UTC(alvo.getUTCFullYear(), alvo.getUTCMonth() + 1, 0)).getUTCDate();
  alvo.setUTCDate(Math.min(d, ultimo));
  return alvo.toISOString().slice(0, 10);
}

export interface EntradaDre {
  vendas: {
    valorCentavos: number;
    custoTotalCentavos: number;
    /** acessórios cobrados do cliente */
    acessoriosCentavos: number;
  }[];
  comissoesEquipeCentavos: number;
  /** soma por grupo dos lançamentos (não cancelados) com competência no período */
  porGrupo: Partial<Record<GrupoDre, { receber: number; pagar: number }>>;
}

export interface LinhaDre {
  chave: string;
  rotulo: string;
  valorCentavos: number;
  /** linhas de resultado (lucro bruto, lucro líquido) */
  total?: boolean;
}

/**
 * DRE gerencial (docs/07): receita e custo dos carros vêm das vendas (custo congelado),
 * comissões da tabela de comissões e o resto dos lançamentos por competência.
 */
export function calcularDre(e: EntradaDre): {
  linhas: LinhaDre[];
  lucroLiquidoCentavos: number;
  carros: number;
} {
  const g = (grupo: GrupoDre) => e.porGrupo[grupo] ?? { receber: 0, pagar: 0 };
  const receitaCarros = e.vendas.reduce((s, v) => s + v.valorCentavos, 0);
  const acessorios = e.vendas.reduce((s, v) => s + v.acessoriosCentavos, 0);
  const custoCarros = e.vendas.reduce((s, v) => s + v.custoTotalCentavos, 0);
  const lucroBruto = receitaCarros + acessorios - custoCarros;
  const financiamento = g('receita_financiamento').receber - g('receita_financiamento').pagar;
  const extras = g('receita_extra').receber - g('receita_extra').pagar;
  const despesa = (grupo: GrupoDre) => g(grupo).pagar - g(grupo).receber;
  const variaveis = despesa('despesa_variavel');
  const fixas = despesa('despesa_fixa');
  const impostos = despesa('imposto');
  const financeiras = despesa('financeiro');
  const outros = despesa('outros');
  const lucroOperacional =
    lucroBruto + financiamento + extras - e.comissoesEquipeCentavos - variaveis;
  const lucroLiquido = lucroOperacional - fixas - impostos - financeiras - outros;
  return {
    carros: e.vendas.length,
    lucroLiquidoCentavos: lucroLiquido,
    linhas: [
      { chave: 'receita_carros', rotulo: 'Venda de carros', valorCentavos: receitaCarros },
      { chave: 'acessorios', rotulo: 'Acessórios', valorCentavos: acessorios },
      {
        chave: 'custo_carros',
        rotulo: '(−) Custo dos carros vendidos',
        valorCentavos: -custoCarros,
      },
      {
        chave: 'lucro_bruto',
        rotulo: 'Lucro bruto dos carros',
        valorCentavos: lucroBruto,
        total: true,
      },
      {
        chave: 'receita_financiamento',
        rotulo: 'Retorno de financiamento',
        valorCentavos: financiamento,
      },
      {
        chave: 'receita_extra',
        rotulo: 'Garantias, seguros e outras receitas',
        valorCentavos: extras,
      },
      {
        chave: 'comissoes',
        rotulo: '(−) Comissões da equipe',
        valorCentavos: -e.comissoesEquipeCentavos,
      },
      { chave: 'despesa_variavel', rotulo: '(−) Despesas variáveis', valorCentavos: -variaveis },
      {
        chave: 'lucro_operacional',
        rotulo: 'Margem de contribuição',
        valorCentavos: lucroOperacional,
        total: true,
      },
      { chave: 'despesa_fixa', rotulo: '(−) Despesas fixas', valorCentavos: -fixas },
      { chave: 'imposto', rotulo: '(−) Impostos', valorCentavos: -impostos },
      { chave: 'financeiro', rotulo: '(−) Despesas financeiras', valorCentavos: -financeiras },
      { chave: 'outros', rotulo: '(−) Outras despesas', valorCentavos: -outros },
      { chave: 'lucro_liquido', rotulo: 'Lucro líquido', valorCentavos: lucroLiquido, total: true },
    ],
  };
}

/**
 * Ponto de equilíbrio: quantos carros o mês precisa vender para pagar as despesas fixas,
 * pela margem de contribuição média por carro (lucro do carro + extras − comissões − variáveis).
 */
export function pontoDeEquilibrio(
  despesasFixasMesCentavos: number,
  margemMediaPorCarroCentavos: number | null,
): number | null {
  if (!margemMediaPorCarroCentavos || margemMediaPorCarroCentavos <= 0) return null;
  return Math.ceil(despesasFixasMesCentavos / margemMediaPorCarroCentavos);
}

// ─────────── Conciliação ───────────

export interface LinhaExtratoBruta {
  data: string;
  descricao: string;
  valorCentavos: number;
  identificador: string;
}

const dataBr = (v: string): string | null => {
  const s = v.trim();
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (br) return `${br[3]}-${br[2]!.padStart(2, '0')}-${br[1]!.padStart(2, '0')}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{8}/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return null;
};

/** Lê extrato OFX (o que os bancos brasileiros exportam): data, valor, descrição e FITID. */
export function lerOfx(texto: string): LinhaExtratoBruta[] {
  const linhas: LinhaExtratoBruta[] = [];
  const blocos = texto.split(/<STMTTRN>/i).slice(1);
  const campo = (b: string, nome: string) =>
    new RegExp(`<${nome}>([^<\\r\\n]*)`, 'i').exec(b)?.[1]?.trim() ?? '';
  for (const b of blocos) {
    const data = dataBr(campo(b, 'DTPOSTED'));
    const valor = reaisParaCentavos(campo(b, 'TRNAMT').replace(',', '.'));
    if (!data || valor === null) continue;
    const fitid = campo(b, 'FITID') || `${data}|${valor}|${campo(b, 'MEMO')}`;
    linhas.push({
      data,
      valorCentavos: valor,
      descricao: (campo(b, 'MEMO') || campo(b, 'NAME') || 'Sem descrição').slice(0, 160),
      identificador: fitid.slice(0, 120),
    });
  }
  return linhas;
}

/** Linhas de planilha/CSV com colunas data, descrição e valor (negativo = saída). */
export function lerExtratoPlanilha(linhas: Record<string, string>[]): {
  linhas: LinhaExtratoBruta[];
  erros: string[];
} {
  const r: LinhaExtratoBruta[] = [];
  const erros: string[] = [];
  const achar = (l: Record<string, string>, nomes: string[]) => {
    const k = Object.keys(l).find((c) =>
      nomes.includes(c.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()),
    );
    return k ? (l[k] ?? '').toString() : '';
  };
  linhas.forEach((l, i) => {
    const data = dataBr(achar(l, ['data', 'data lancamento', 'dt']));
    const valor = reaisParaCentavos(achar(l, ['valor', 'valor (r$)', 'montante']));
    const descricao = achar(l, ['descricao', 'historico', 'lancamento', 'memo']).trim();
    if (!data || valor === null || valor === 0) {
      erros.push(`Linha ${i + 2}: data ou valor inválido`);
      return;
    }
    r.push({
      data,
      valorCentavos: valor,
      descricao: (descricao || 'Sem descrição').slice(0, 160),
      identificador: `${data}|${valor}|${descricao}|${i}`.slice(0, 120),
    });
  });
  return { linhas: r, erros };
}

/**
 * Sugere o lançamento de cada linha do extrato: mesmo valor (entrada = receber, saída = pagar)
 * e data até 5 dias de distância; cada lançamento só serve para uma linha.
 */
export function sugerirConciliacao(
  linhas: { id: string; data: string; valorCentavos: number }[],
  lancamentos: { id: string; tipo: TipoLancamento; valorCentavos: number; data: string }[],
  janelaDias = 5,
): Record<string, string | null> {
  const usados = new Set<string>();
  const r: Record<string, string | null> = {};
  const dias = (a: string, b: string) =>
    Math.abs(new Date(`${a}T00:00:00Z`).getTime() - new Date(`${b}T00:00:00Z`).getTime()) /
    86_400_000;
  for (const l of linhas) {
    const tipo: TipoLancamento = l.valorCentavos > 0 ? 'receber' : 'pagar';
    const candidatos = lancamentos
      .filter(
        (x) =>
          !usados.has(x.id) &&
          x.tipo === tipo &&
          x.valorCentavos === Math.abs(l.valorCentavos) &&
          dias(x.data, l.data) <= janelaDias,
      )
      .sort((a, b) => dias(a.data, l.data) - dias(b.data, l.data));
    const escolhido = candidatos[0]?.id ?? null;
    if (escolhido) usados.add(escolhido);
    r[l.id] = escolhido;
  }
  return r;
}

// ─────────── Importador de contas a pagar e receber ───────────

export const CAMPOS_CONTA = [
  {
    campo: 'descricao',
    rotulo: 'Descrição',
    obrigatorio: true,
    apelidos: ['descricao', 'historico', 'conta', 'titulo'],
  },
  {
    campo: 'valor',
    rotulo: 'Valor',
    obrigatorio: true,
    apelidos: ['valor', 'valor (r$)', 'total'],
  },
  {
    campo: 'vencimento',
    rotulo: 'Vencimento',
    obrigatorio: true,
    apelidos: ['vencimento', 'data vencimento', 'venc', 'data'],
  },
  {
    campo: 'tipo',
    rotulo: 'Pagar ou receber',
    obrigatorio: false,
    apelidos: ['tipo', 'natureza', 'pagar/receber'],
  },
  {
    campo: 'categoria',
    rotulo: 'Categoria',
    obrigatorio: false,
    apelidos: ['categoria', 'plano de contas', 'classificacao'],
  },
  {
    campo: 'contraparte',
    rotulo: 'Fornecedor ou cliente',
    obrigatorio: false,
    apelidos: ['fornecedor', 'cliente', 'favorecido', 'contraparte'],
  },
  {
    campo: 'pagoEm',
    rotulo: 'Pago em',
    obrigatorio: false,
    apelidos: ['pago em', 'data pagamento', 'pagamento', 'baixa'],
  },
] as const;
export type CampoConta = (typeof CAMPOS_CONTA)[number]['campo'];
const camposConta = CAMPOS_CONTA.map((c) => c.campo) as [CampoConta, ...CampoConta[]];

export const mapeamentoContasSchema = z
  .record(z.string(), z.enum(camposConta).nullable())
  .refine(
    (m) =>
      ['descricao', 'valor', 'vencimento'].every((c) => Object.values(m).includes(c as CampoConta)),
    {
      message: 'Mapeie descrição, valor e vencimento',
    },
  )
  .refine(
    (m) => {
      const u = Object.values(m).filter(Boolean);
      return new Set(u).size === u.length;
    },
    { message: 'Cada campo só pode vir de uma coluna' },
  );

const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[_\-.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export function sugerirMapeamentoContas(cabecalhos: string[]): Record<string, CampoConta | null> {
  const r: Record<string, CampoConta | null> = {};
  const usados = new Set<string>();
  for (const cab of cabecalhos) {
    const n = normalizar(cab);
    const achado = CAMPOS_CONTA.find(
      (c) => !usados.has(c.campo) && c.apelidos.some((a) => normalizar(a) === n),
    );
    r[cab] = achado?.campo ?? null;
    if (achado) usados.add(achado.campo);
  }
  return r;
}

export interface ContaConvertida {
  descricao: string;
  valorCentavos: number;
  vencimento: string;
  tipo: TipoLancamento;
  categoria: string | null;
  contraparte: string | null;
  pagoEm: string | null;
}

/** Valor negativo ou tipo "pagar/despesa/saída" = conta a pagar; o resto, a receber. */
export function converterLinhaConta(
  bruta: Record<string, string>,
  mapa: Record<string, CampoConta | null>,
): { ok: true; valor: ContaConvertida } | { ok: false; erros: string[] } {
  const v: Partial<Record<CampoConta, string>> = {};
  for (const [col, campo] of Object.entries(mapa))
    if (campo) v[campo] = (bruta[col] ?? '').toString().trim();
  const erros: string[] = [];
  const descricao = (v.descricao ?? '').slice(0, 160);
  if (descricao.length < 2) erros.push('Descrição vazia');
  const valor = reaisParaCentavos(v.valor ?? '');
  if (valor === null || valor === 0) erros.push(`Valor inválido: "${v.valor ?? ''}"`);
  const vencimento = dataBr(v.vencimento ?? '');
  if (!vencimento) erros.push(`Vencimento inválido: "${v.vencimento ?? ''}"`);
  const pagoEm = v.pagoEm ? dataBr(v.pagoEm) : null;
  if (v.pagoEm && !pagoEm) erros.push(`Data de pagamento inválida: "${v.pagoEm}"`);
  const t = normalizar(v.tipo ?? '');
  const tipoTexto = /^(pagar|despesa|saida|debito|d)$/.test(t)
    ? 'pagar'
    : /^(receber|receita|entrada|credito|c)$/.test(t)
      ? 'receber'
      : null;
  if (v.tipo && !tipoTexto) erros.push(`Tipo inválido: "${v.tipo}" (use pagar ou receber)`);
  if (erros.length) return { ok: false, erros };
  return {
    ok: true,
    valor: {
      descricao,
      valorCentavos: Math.abs(valor!),
      vencimento: vencimento!,
      tipo: tipoTexto ?? (valor! < 0 ? 'pagar' : 'receber'),
      categoria: v.categoria || null,
      contraparte: v.contraparte?.slice(0, 120) || null,
      pagoEm,
    },
  };
}

// ─────────── Financiamento ───────────

export const STATUS_PROPOSTA_FINANCIAMENTO = [
  'rascunho',
  'enviada',
  'em_analise',
  'aprovada',
  'recusada',
  'cancelada',
  'paga',
] as const;
export type StatusPropostaFinanciamento = (typeof STATUS_PROPOSTA_FINANCIAMENTO)[number];
export const NOME_STATUS_FINANCIAMENTO: Record<StatusPropostaFinanciamento, string> = {
  rascunho: 'Rascunho',
  enviada: 'Enviada',
  em_analise: 'Em análise',
  aprovada: 'Aprovada',
  recusada: 'Recusada',
  cancelada: 'Cancelada',
  paga: 'Paga pelo banco',
};

export const bancoFinanciamentoSchema = z.object({
  nome: texto(60).min(2),
  /** retorno em centésimos de ponto sobre o valor financiado (150 = 1,5%) */
  retornoPercentual: z.number().int().min(0).max(3_000),
  integracao: z.enum(['manual', 'mock']).default('manual'),
});

export const propostaFinanciamentoSchema = z
  .object({
    bancoId: z.string().uuid(),
    clienteId: z.string().uuid(),
    veiculoId: z.string().uuid(),
    leadId: z.string().uuid().optional(),
    valorVeiculoCentavos: positivo,
    entradaCentavos: centavos,
    prazoMeses: z.number().int().min(6).max(72),
    taxaMensal: z.number().int().min(0).max(1_000).optional(),
  })
  .refine((d) => d.entradaCentavos < d.valorVeiculoCentavos, {
    message: 'A entrada precisa ser menor que o valor do carro',
  });

export const propostaRespostaSchema = z.object({
  status: z.enum(['em_analise', 'aprovada', 'recusada', 'cancelada', 'paga']),
  motivo: textoOpc(300),
  taxaMensal: z.number().int().min(0).max(1_000).optional(),
  parcelaCentavos: positivo.optional(),
  valorFinanciadoCentavos: positivo.optional(),
});

/** Parcela pela tabela Price. Taxa em centésimos de ponto ao mês. */
export function parcelaPrice(
  valorCentavos: number,
  taxaMensal: number,
  prazoMeses: number,
): number {
  const i = taxaMensal / 10_000;
  if (i === 0) return Math.ceil(valorCentavos / prazoMeses);
  return parcelaPriceCentavos(valorCentavos, i, prazoMeses);
}

export const CONSENTIMENTO_OPEN_FINANCE =
  'Autorizo a loja a consultar meus dados financeiros (Open Finance) uma única vez, para analisar o crédito desta compra.';
export const analiseCreditoSchema = z.object({
  consentimento: z.literal(true, {
    error: 'O cliente precisa autorizar a consulta',
  }),
});

export const FAIXAS_CREDITO = ['alta', 'media', 'baixa'] as const;
export type FaixaCredito = (typeof FAIXAS_CREDITO)[number];

// ─────────── Garantia estendida e seguro (parceiro habilitado) ───────────

export const TIPOS_PROTECAO = ['garantia', 'seguro'] as const;
export type TipoProtecao = (typeof TIPOS_PROTECAO)[number];
export interface CotacaoProtecao {
  id: string;
  tipo: TipoProtecao;
  parceiro: string;
  descricao: string;
  /** preço para o cliente */
  valorCentavos: number;
  /** quanto a loja recebe do parceiro */
  comissaoCentavos: number;
}

// ─────────── Pagamentos ───────────

export const cobrancaPixSchema = z.object({
  lancamentoId: z.string().uuid(),
  expiraEmHoras: z
    .number()
    .int()
    .min(1)
    .max(24 * 30)
    .default(48),
});

export const pixAutomaticoSchema = z.object({
  filialId: z.string().uuid(),
  categoriaId: z.string().uuid().optional(),
  clienteId: z.string().uuid(),
  descricao: texto(120).min(2),
  valorParcelaCentavos: positivo,
  parcelas: z.number().int().min(2).max(60),
  diaCobranca: z.number().int().min(1).max(28),
  primeiroMes: z.string().regex(/^\d{4}-\d{2}$/, 'Mês inválido (AAAA-MM)'),
});
